import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {v4 as uuidv4} from 'uuid';
import MotifButton from '@ey/motif-react-wrapper/Button';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {contentIcAddCircle24px} from '@ey/motif-react-wrapper/assets/icons';
import OutputText from '@ey/canvascoreservices/OutputText';
import {setCustomTags} from '../../../../../../../actions/ContactDB';
import {updateDisplayData} from '../../../../../../../actions/Metrics/dashboardContextAction';
import {labels} from '../../../../../../../util/utils';
import Utility from '../../../../../../../util/utilityfunctions';
import BorderBox from '../../../../../../Common/BorderBox';
import {tagTypeAction, defaultClientTagColor, tagTypeAPI} from './utils/constants';
import normalizeTags from './utils/normalizeTags';
import TagsItem from './TagsItem';
import normalizeTagsEntities from './utils/normalizeTagsEntities';
import Modal from '../../../../../../CoreWrappers/Modal';
import HrAlt from '../../../../../../Common/Hr/HrAlt';
import StyledModalContent from './StyledModalContent';
import ListWithScroll from '../../../../../../Common/ListWithScroll';
import Button from '../../../../../../CoreWrappers/Button';
import {MotifContentIcAddCircleOutline24px} from '@ey-xd/motif-icon';

const TagsList = ({showTagsModal, onModalClose}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [tags, setTags] = useState([]); // data from API
	const [actionTags, setActionTags] = useState([]); // data that was modified (new, edit, deleted...), then API call
	const [showErrors, setShowErrors] = useState(false);

	const projectId = Utility.getProjectIdFromUrl();
	const tagsStore = useSelector((store) => store.project.tags);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);
	const entities = useSelector(({dashboardContext}) => dashboardContext?.entities);
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);

	const dispatch = useDispatch();

	useEffect(() => {
		if (tags?.length < 1) {
			const normalizedTagsList = normalizeTags(tagsStore);
			const tagsList = normalizedTagsList ? normalizedTagsList : [];
			setTags(tagsList);
		}
	}, [tagsStore]);

	const tagIsEdited = (tag) => tag.action === tagTypeAction.existing;
	const tagIsCreated = (tag) => tag.action === tagTypeAction.create;

	const tagsHasErrors = (tags) => {
		let result = tags.some((tag) => {
			return _.isEmpty(tag.colorCode) || tag.colorCode === defaultClientTagColor || _.isEmpty(tag.name.trim());
		});

		setShowErrors(result);
		return result;
	};

	const closeModal = () => {
		if (onModalClose) {
			onModalClose();
		}
	};

	const handlerOnItemChange = (tag) => {
		let actionTagsItems = [...actionTags];
		let tagsItems = [...tags];
		let tagIndexRendered = tagsItems.findIndex((x) => x.id === tag.id);
		if (tagIndexRendered > -1) {
			tagsItems[tagIndexRendered] = tag;
			setTags(tagsItems);
		}
		if (tagIsCreated(tag)) {
			let tagIndex = actionTagsItems.findIndex((x) => x.id === tag.id);
			if (tagIndex > -1) {
				actionTagsItems[tagIndex] = tag;
				setActionTags(actionTagsItems);
			}
		}

		if (tagIsEdited(tag)) {
			tag.action = tagTypeAction.edit;
			addExistingTagToActionTags(tag, actionTagsItems);
		}
	};

	//this method is used by the edit and delete
	const addExistingTagToActionTags = (tag, actionTagsItems) => {
		// check if tag exist in actionTags
		let tagIndex = actionTagsItems.findIndex((x) => x.id === tag.id);
		if (tagIndex > -1) {
			// replace
			actionTagsItems[tagIndex] = tag;
		} else {
			// add
			actionTagsItems.push(tag);
		}
		setActionTags(actionTagsItems);
	};

	const handlerOnItemDelete = (tag) => {
		let actionTagsItems = [...actionTags];
		let tagItems = [...tags];

		let tagsResult = tagItems.filter((x) => x.id !== tag.id);
		setTags(tagsResult);
		if (tagIsCreated(tag)) {
			//Remove tag from actionTags list
			let actionTagsResult = actionTagsItems.filter((x) => x.id !== tag.id);
			setActionTags(actionTagsResult);
		} else if (tagIsEdited(tag)) {
			//Delete Tag
			tag.action = tagTypeAction.delete;
			addExistingTagToActionTags(tag, actionTagsItems);
		}
	};

	const handlerOnNewTagClick = () => {
		const newUuid = uuidv4();

		const newTag = {
			name: '',
			id: newUuid,
			colorCode: '',
			isCustom: true,
			action: tagTypeAction.create // New
		};

		// render new tag in list
		setTags([...tags, newTag]);

		// new tag added to be created
		setActionTags([...actionTags, newTag]);

		//Until save changes button pressed, dont show validations.
		setShowErrors(false);
	};

	const handlerOnOkClick = () => {
		// check if tags are valid
		if (!tagsHasErrors(actionTags)) {
			// avoid to call API again
			if (!isLoading) {
				setShowErrors(false);
				saveData();
			}
		}
	};

	const saveData = () => {
		setIsLoading(true);

		const deleteArray = actionTags
			.filter((t) => t.action === tagTypeAction.delete)
			.map((tag) => {
				return {
					id: tag.id,
					name: tag.name,
					color: tag.colorCode,
					operationType: tagTypeAPI[tag.action]
				};
			});

		const editedArray = tags
			.filter((t) => t.action !== tagTypeAction.existing)
			.map((tag) => {
				return {
					id: tag.id,
					name: tag.name,
					color: tag.colorCode,
					operationType: tagTypeAPI[tag.action]
				};
			});

		const silentRefreshArray = tags.map((tag) => {
			return {
				id: tag.id,
				name: tag.name,
				color: tag.colorCode,
				isCustom: tag.isCustom,
				isDeleted: false
			};
		});

		const actionArray = [...deleteArray, ...editedArray];
		dispatch(setCustomTags(projectId, [...actionArray], silentRefreshArray))
			.then(() => {
				dispatch(updateDisplayData(engagements, normalizeTagsEntities(entities, actionArray), orgChart, true))
					.then(() => {
						setIsLoading(false);
						closeModal();
					})
					.catch(() => {
						setIsLoading(false);
					});
			})
			.catch((e) => {
				setIsLoading(false);
			});
	};

	const handlerOnModalHide = () => {
		closeModal();
	};

	const sendShowError = (tagId) => {
		if (actionTags.some((tag) => tag.id === tagId)) {
			return showErrors;
		} else {
			return false;
		}
	};

	const renderInstructionText = () => (
		<>
			<OutputText light>{labels.manageTagInstructional}</OutputText>
			<HrAlt />
		</>
	);

	const renderCustomTagsHeader = () => <OutputText>{labels.customTagsHeader}</OutputText>;

	const renderTag = (tag) => (
		<TagsItem
			key={tag.id}
			propTag={tag}
			showErrors={sendShowError(tag.id)}
			onItemChange={handlerOnItemChange}
			onItemDelete={handlerOnItemDelete}
		/>
	);

	const renderTagsList = () => {
		const props = {
			list: tags,
			item: renderTag,
			emptyTitle: labels.emptyCustomTagsList,
			enableScroll: false
		};

		return <ListWithScroll {...props} />;
	};

	const renderCreateTagButton = () => {
		const props = {
			variant: 'text-alt',
			onClick: handlerOnNewTagClick,
			label: labels.tagCreate,
			svg: MotifContentIcAddCircleOutline24px
		};

		return (
			<div>
				<Button {...props} />
			</div>
		);
	};

	const props = {
		labels: {
			modalConfirmBtnLabel: labels.save,
			modalCancelBtnLabel: labels.cancelLabel,
			modalCloseBtnTitletip: labels.closeTitleModal
		},
		headerText: labels.manageTagTitle,
		show: showTagsModal,
		onClose: handlerOnModalHide,
		onOkClick: handlerOnOkClick,
		modalsize: 'small',
		isLoading
	};

	return (
		<Modal {...props}>
			<StyledModalContent>
				{renderInstructionText()}
				{renderCustomTagsHeader()}
				<div className="error-message-clearence">
					{renderTagsList()}
					{renderCreateTagButton()}
				</div>
			</StyledModalContent>
		</Modal>
	);
};

TagsList.propTypes = {
	showTagsModal: PropTypes.bool,
	onModalOpen: PropTypes.func,
	onModalClose: PropTypes.func,
	externalOpenModal: PropTypes.bool
};

export default TagsList;
